"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import { Section } from "@/components/ui/Section";
import { Typography } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";
import { cn } from "@/lib/utils";

export type IndustryHubCategory =
  | "retail"
  | "restaurant"
  | "hospitality"
  | "manufacturing"
  | "transportation"
  | "finance"
  | "entertainment"
  | "personalcare"
  | "healthcare"
  | "education"
  | "workplace"
  | "civic";

export type IndustryHubSection = "core" | "extended" | "subpage";

export type IndustryHubCard = {
  title: string;
  description: string;
  sessions?: string;
  section: IndustryHubSection;
  ctaText: string;
  ctaLink: string;
  imageAlt: string;
  imageUrl?: string;
  imageSrc?: string;
  category: IndustryHubCategory;
  /** Optional badge label shown on the card. Falls back to the category label. */
  tag?: string;
  searchTerms: string;
};

type FilterKey = "all" | IndustryHubCategory;

const FILTER_LABELS: Record<FilterKey, string> = {
  all: "All",
  retail: "Retail",
  restaurant: "Restaurant",
  hospitality: "Hospitality",
  manufacturing: "Manufacturing",
  transportation: "Transportation",
  finance: "Finance",
  entertainment: "Entertainment",
  personalcare: "Personal Care & Fitness",
  healthcare: "Healthcare",
  education: "Education",
  workplace: "Workplace",
  civic: "Public",
};

const CATEGORY_BADGE_STYLES: Record<IndustryHubCategory, string> = {
  retail: "bg-indigo-500 ring-indigo-500/20",
  restaurant: "bg-rose-500 ring-rose-500/20",
  hospitality: "bg-fuchsia-500 ring-fuchsia-500/20",
  manufacturing: "bg-cyan-500 ring-cyan-500/20",
  transportation: "bg-sky-500 ring-sky-500/20",
  finance: "bg-lime-500 ring-lime-500/20",
  entertainment: "bg-amber-500 ring-amber-500/20",
  personalcare: "bg-pink-500 ring-pink-500/20",
  healthcare: "bg-emerald-500 ring-emerald-500/20",
  education: "bg-teal-500 ring-teal-500/20",
  workplace: "bg-blue-500 ring-blue-500/20",
  civic: "bg-violet-500 ring-violet-500/20",
};

const FILTER_KEYS: FilterKey[] = [
  "all",
  "retail",
  "restaurant",
  "hospitality",
  "manufacturing",
  "workplace",
  "transportation",
  "finance",
  "entertainment",
  "personalcare",
  "healthcare",
  "education",
  "civic",
];

interface IndustriesHubGridProps {
  industries: IndustryHubCard[];
  imgBaseUrl: string;
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 10l2.5 2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IndustryCard({
  industry,
  imgBaseUrl,
}: {
  industry: IndustryHubCard;
  imgBaseUrl: string;
}) {
  return (
    <article className="group flex h-full flex-col rounded-xl overflow-clip border border-gray-200 bg-white">
      <div className="relative">
        <div className="relative aspect-video overflow-hidden">
          <Image src={industry.imageSrc ?? imgBaseUrl + industry.imageUrl!} alt={industry.imageAlt} title={industry.imageAlt} fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
          <Typography
            variant="small"
            as="span"
            className={cn(
              "absolute left-3 top-3 z-1 text-white rounded-full px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider leading-none ring-1 ring-inset ",
              CATEGORY_BADGE_STYLES[industry.category]
            )}
          >
            {industry.tag ?? FILTER_LABELS[industry.category]}
          </Typography>
        </div>
      </div>

      <div className="flex flex-col px-5 pb-5 pt-4">
        <Typography variant="h3" as="h2" className="mb-2 text-lg">
          {industry.title}
        </Typography>

        <Typography
          variant="small"
          as="p"
          className="line-clamp-3 min-h-18 leading-relaxed text-slate-600"
        >
          {industry.description}
        </Typography>

        <div className="mt-5 border-t border-slate-100 pt-4">
          <Link href={industry.ctaLink} className="inline-flex">
            <NewButton
              variant="linkPrimary"
              size="sm"
              arrow="grow"
              arrowColorClass="stroke-accent"
              className="h-auto w-auto p-0"
              asChild
            >
              {industry.ctaText}
            </NewButton>
          </Link>
        </div>
      </div>
    </article>
  );
}

function IndustryGrid({
  items,
  imgBaseUrl,
}: {
  items: IndustryHubCard[];
  imgBaseUrl: string;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-7 lg:grid-cols-3 lg:gap-8">
      {items.map(industry => (
        <IndustryCard
          key={`${industry.section}-${industry.ctaLink}-${industry.title}`}
          industry={industry}
          imgBaseUrl={imgBaseUrl}
        />
      ))}
    </div>
  );
}


export function IndustriesHubGrid({ industries, imgBaseUrl }: IndustriesHubGridProps) {
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showBackTop, setShowBackTop] = useState(false);

  const filterCounts = useMemo(() => {
    const counts: Record<FilterKey, number> = {
      all: industries.length,
      retail: 0,
      restaurant: 0,
      hospitality: 0,
      manufacturing: 0,
      transportation: 0,
      finance: 0,
      entertainment: 0,
      personalcare: 0,
      healthcare: 0,
      education: 0,
      workplace: 0,
      civic: 0,
    };
    for (const industry of industries) {
      counts[industry.category] += 1;
    }
    return counts;
  }, [industries]);

  const visibleIndustries = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return industries.filter(industry => {
      const matchesFilter = activeFilter === "all" || industry.category === activeFilter;
      const haystack =
        `${industry.searchTerms} ${industry.title} ${industry.description}`.toLowerCase();
      const matchesSearch = !q || haystack.includes(q);
      return matchesFilter && matchesSearch;
    });
  }, [industries, activeFilter, searchQuery]);

  const handleFilterChange = useCallback((key: FilterKey) => {
    setActiveFilter(key);
    setSearchQuery("");
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (value.trim()) {
      setActiveFilter("all");
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setShowBackTop(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Section variant="default" background="gray">
      <div className="sticky top-20 z-10 rounded-xl border-b border-gray-200 bg-white px-4">
        <div className="flex flex-col gap-3 py-2 md:flex-row md:items-center md:gap-0 md:py-0">
          <div
            className="flex items-center gap-0 overflow-x-auto scrollbar-none md:min-w-0 md:flex-1"
            role="tablist"
            aria-label="Filter industries by category"
          >
            {FILTER_KEYS.map(key => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeFilter === key && !searchQuery.trim()}
                onClick={() => handleFilterChange(key)}
                className={cn(
                  "group shrink-0 border-b-2 px-3 py-3 whitespace-nowrap transition-colors",
                  activeFilter === key && !searchQuery.trim()
                    ? "border-primary"
                    : "border-transparent hover:border-primary/30"
                )}
              >
                <Typography
                  variant="small"
                  as="span"
                  className={cn(
                    "text-[13px]",
                    activeFilter === key && !searchQuery.trim()
                      ? "font-semibold text-primary"
                      : "font-medium text-slate-600 group-hover:text-primary"
                  )}
                >
                  {FILTER_LABELS[key]}
                </Typography>
                <Typography
                  variant="small"
                  as="span"
                  className={cn(
                    "ml-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none",
                    activeFilter === key && !searchQuery.trim()
                      ? "bg-blue-50 text-primary"
                      : "bg-slate-100 text-gray-400"
                  )}
                >
                  {filterCounts[key]}
                </Typography>
              </button>
            ))}
          </div>
          <div className="w-full shrink-0 pb-1 md:ml-auto md:w-auto md:pb-0 md:pl-4">
            <label className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-slate-50 px-3 py-2 focus-within:border-primary md:py-1.5">
              <span className="text-gray-400">
                <SearchIcon />
              </span>
              <input
                type="search"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Find your industry..."
                className="min-w-0 flex-1 border-0 bg-transparent text-sm text-slate-800 outline-none placeholder:text-gray-400 md:w-40 md:flex-none"
                aria-label="Find your industry"
              />
            </label>
          </div>
        </div>
      </div>

      <div className="pb-16 pt-10">
        {visibleIndustries.length === 0 ? (
          <div className="py-16 text-center">
            <Typography variant="h3" as="h3" className="mb-2">
              No industries found
            </Typography>
            <Typography variant="body" as="p">
              Try a different search term or browse all industries.
            </Typography>
          </div>
        ) : (
          <IndustryGrid items={visibleIndustries} imgBaseUrl={imgBaseUrl} />
        )}
      </div>

      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        className={cn(
          "fixed bottom-7 right-7 z-[99] flex h-11 w-11 items-center justify-center rounded-full bg-slate-800 text-white shadow-lg transition-all hover:-translate-y-0.5",
          showBackTop ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path
            d="M8 12V4M4 8l4-4 4 4"
            stroke="white"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </Section>
  );
}
